import { generateBadge, type BadgeSpec } from '@fp/badges';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useState } from 'react';
import { Badge, BadgeEditor } from '../components/badges';
import { RulesEditor, rulesValid } from '../components/RulesEditor';
import { Topbar } from '../components/Shell';
import { Confirm, Empty, ErrorBox, Field, Loading, Modal, useAction, useToast } from '../components/ui';
import { del, get, patch, post, put, qs } from '../lib/api';
import type { Competition, GameSettings, Player, ScoringRules, Team } from '../lib/types';
import { FeedTeamPicker, FollowLeagueModal } from './Feed';

// ================================================================ competitions

export function CompetitionsPage() {
  const q = useQuery({ queryKey: ['competitions'], queryFn: () => get<{ competitions: Competition[] }>('/admin/competitions') });
  const [editing, setEditing] = useState<Competition | 'new' | null>(null);
  const [following, setFollowing] = useState(false);
  return (
    <>
      <Topbar
        title="Competitions"
        sub="Leagues and cups, their points tables, lock times and generated badges"
        actions={
          <>
            <button type="button" className="btn btn-primary" onClick={() => setFollowing(true)}>
              Follow a league from the data feed
            </button>
            <button type="button" className="btn" onClick={() => setEditing('new')}>
              New competition by hand
            </button>
          </>
        }
      />
      <div className="page">
        <div className="card">
          {q.error && <ErrorBox error={q.error} />}
          {q.isLoading ? (
            <Loading />
          ) : q.data?.competitions.length === 0 ? (
            <Empty>No competitions yet. Create one here, or type a new name when creating a match.</Empty>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Competition</th>
                    <th>Season</th>
                    <th>Type</th>
                    <th>Points (exact / diff / result / HT / scorer)</th>
                    <th>Locks</th>
                    <th className="right">Matches</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {q.data?.competitions.map((c) => (
                    <tr key={c.id} className={`clickable${c.active ? '' : ' dim'}`} onClick={() => setEditing(c)}>
                      <td>
                        <span className="row" style={{ flexWrap: 'nowrap' }}>
                          <Badge spec={c.badge} size={28} />
                          <strong>{c.name}</strong>
                          {c.shortName && <span className="muted">{c.shortName}</span>}
                          {c.provider !== 'manual' && <span className="pill" title="Fixtures can be imported and follow the feed live">Data feed</span>}
                        </span>
                      </td>
                      <td>{c.season}</td>
                      <td>{c.isCup ? 'Cup' : 'League'}</td>
                      <td className="num">
                        {c.rules.exactScore} / {c.rules.goalDifference} / {c.rules.correctOutcome} / +{c.rules.halfTimeOutcome} / +{c.rules.firstScorer}
                      </td>
                      <td>{c.lockMinutesBeforeKickoff ? `${c.lockMinutesBeforeKickoff} min before` : 'At kickoff'}</td>
                      <td className="right num">{c.matchCount}</td>
                      <td className="right">{!c.active && <span className="pill">Inactive</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      {editing && <CompetitionForm competition={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
      {following && <FollowLeagueModal onClose={() => setFollowing(false)} />}
    </>
  );
}

function CompetitionForm({ competition, onClose }: { competition: Competition | null; onClose: () => void }) {
  const qc = useQueryClient();
  const settings = useQuery({ queryKey: ['settings'], queryFn: () => get<{ settings: GameSettings }>('/admin/settings') });
  const s = settings.data?.settings;
  const [name, setName] = useState(competition?.name ?? '');
  const [shortName, setShortName] = useState(competition?.shortName ?? '');
  const [season, setSeason] = useState(competition?.season ?? '');
  const [area, setArea] = useState(competition?.area ?? '');
  const [isCup, setIsCup] = useState(competition?.isCup ?? false);
  const [active, setActive] = useState(competition?.active ?? true);
  const [lock, setLock] = useState(competition?.lockMinutesBeforeKickoff ?? s?.defaultLockMinutes ?? 0);
  const [rules, setRules] = useState<ScoringRules | null>(competition?.rules ?? null);
  const [badge, setBadge] = useState<BadgeSpec | null>(competition?.badge ?? null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const { busy, run } = useAction();
  const toast = useToast();
  const effRules = rules ?? s?.defaultRules ?? null;
  const effBadge = badge ?? generateBadge({ kind: 'competition', name: name || '?', shortName });
  const rulesChanged = competition && effRules && JSON.stringify(effRules) !== JSON.stringify(competition.rules);
  return (
    <Modal
      wide
      title={competition ? `Edit ${competition.name}` : 'New competition'}
      onClose={onClose}
      footer={
        <>
          {competition && (
            <button type="button" className="btn btn-danger" style={{ marginRight: 'auto' }} onClick={() => setConfirmDelete(true)}>
              Delete
            </button>
          )}
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy || !effRules || !rulesValid(effRules) || !name.trim()}
            onClick={() =>
              run(async () => {
                const body = { name, shortName: shortName || null, season: season || undefined, area: area || null, isCup, active, lockMinutesBeforeKickoff: lock, rules: effRules, badge: effBadge };
                if (competition) {
                  const r = await patch<{ rescored: number }>(`/admin/competitions/${competition.id}`, body);
                  if (r.rescored) toast(`Points table changed: ${r.rescored} match(es) re-scored`);
                } else await post('/admin/competitions', body);
                await qc.invalidateQueries({ queryKey: ['competitions'] });
                onClose();
              }, competition ? 'Competition saved' : 'Competition created')
            }
          >
            {competition ? 'Save' : 'Create'}
          </button>
        </>
      }
    >
      <div className="form-grid">
        <Field label="Name">
          <input className="input" value={name} maxLength={60} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Short name">
          <input className="input" value={shortName} maxLength={12} onChange={(e) => setShortName(e.target.value)} />
        </Field>
        <Field label="Season" hint={`Default ${s?.currentSeason ?? ''}`}>
          <input className="input" value={season} placeholder={s?.currentSeason} onChange={(e) => setSeason(e.target.value)} />
        </Field>
        <Field label="Country or region">
          <input className="input" value={area} maxLength={56} onChange={(e) => setArea(e.target.value)} />
        </Field>
        <Field label="Predictions lock" hint="Minutes before kickoff (0 = at kickoff)">
          <input className="input" type="number" min={0} max={1440} value={lock} onChange={(e) => setLock(Math.trunc(Number(e.target.value)))} />
        </Field>
        <div className="stack" style={{ gap: 4, justifyContent: 'flex-end' }}>
          <label className="check">
            <input type="checkbox" checked={isCup} onChange={(e) => setIsCup(e.target.checked)} /> Knockout cup
          </label>
          <label className="check">
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /> Active
          </label>
        </div>
      </div>
      <h3>Points table</h3>
      {effRules ? <RulesEditor value={effRules} onChange={setRules} /> : <Loading />}
      {rulesChanged && <div className="note-box">Saving re-scores every scored match in this competition. Running totals stay exact.</div>}
      <h3>Badge</h3>
      <BadgeEditor value={effBadge} onChange={setBadge} name={name} />
      {confirmDelete && competition && (
        <Confirm
          title={`Delete ${competition.name}?`}
          body="Only a competition with no matches or leagues can be deleted. Otherwise make it inactive."
          confirmLabel="Delete"
          danger
          onClose={() => setConfirmDelete(false)}
          onConfirm={async () => {
            await del(`/admin/competitions/${competition.id}`);
            await qc.invalidateQueries({ queryKey: ['competitions'] });
            onClose();
          }}
        />
      )}
    </Modal>
  );
}

// ================================================================ teams

export function TeamsPage() {
  const [q, setQ] = useState('');
  const teams = useQuery({ queryKey: ['teams', q], queryFn: () => get<{ teams: Team[] }>(`/admin/teams${qs({ q })}`) });
  const [creating, setCreating] = useState<'feed' | 'manual' | null>(null);
  const navigate = useNavigate();
  return (
    <>
      <Topbar
        title="Teams and squads"
        sub="Badges are generated from each team's colours. No real crests are stored or shown."
        actions={
          <>
            <button type="button" className="btn btn-primary" onClick={() => setCreating('feed')}>
              New team from the data feed
            </button>
            <button type="button" className="btn" onClick={() => setCreating('manual')}>
              New team by hand
            </button>
          </>
        }
      >
        <input className="input" placeholder="Search teams" aria-label="Search teams" value={q} onChange={(e) => setQ(e.target.value)} style={{ width: 220 }} />
      </Topbar>
      <div className="page">
        {teams.error && <ErrorBox error={teams.error} />}
        {teams.isLoading && <Loading />}
        {teams.data?.teams.length === 0 && <Empty>No teams found.</Empty>}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
          {teams.data?.teams.map((t) => (
            <button
              key={t.id}
              type="button"
              className="card"
              style={{ padding: 14, display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', textAlign: 'left' }}
              onClick={() => navigate({ to: '/teams/$teamId', params: { teamId: String(t.id) } })}
            >
              <Badge spec={t.badge} size={44} />
              <span className="stack" style={{ gap: 2, minWidth: 0 }}>
                <strong style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.name}</strong>
                <span className="muted small">
                  {t.squadSize ?? 0} players · {t.matchCount ?? 0} matches
                </span>
              </span>
            </button>
          ))}
        </div>
      </div>
      {creating === 'manual' && <TeamForm onClose={() => setCreating(null)} />}
      {creating === 'feed' && (
        <Modal wide title="New team from the data feed" onClose={() => setCreating(null)}>
          <FeedTeamPicker
            onDone={(t) => {
              setCreating(null);
              navigate({ to: '/teams/$teamId', params: { teamId: String(t.id) } });
            }}
          />
        </Modal>
      )}
    </>
  );
}

function TeamForm({ team, onClose }: { team?: Team; onClose: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [name, setName] = useState(team?.name ?? '');
  const [shortName, setShortName] = useState(team?.shortName ?? '');
  const [country, setCountry] = useState(team?.country ?? '');
  const [badge, setBadge] = useState<BadgeSpec | null>(team?.badge ?? null);
  const { busy, run } = useAction();
  const eff = badge ?? generateBadge({ kind: 'team', name: name || '?', shortName });
  return (
    <Modal
      wide
      title={team ? `Edit ${team.name}` : 'New team'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy || !name.trim()}
            onClick={() =>
              run(async () => {
                const body = { name, shortName: shortName || null, country: country || null, badge: eff };
                if (team) await patch(`/admin/teams/${team.id}`, body);
                else {
                  const r = await post<{ team: Team }>('/admin/teams', body);
                  navigate({ to: '/teams/$teamId', params: { teamId: String(r.team.id) } });
                }
                await qc.invalidateQueries({ queryKey: ['teams'] });
                await qc.invalidateQueries({ queryKey: ['team'] });
                onClose();
              }, team ? 'Team saved' : 'Team created')
            }
          >
            {team ? 'Save' : 'Create team'}
          </button>
        </>
      }
    >
      <div className="form-grid">
        <Field label="Name">
          <input className="input" value={name} maxLength={60} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Short name" hint="Up to 12 characters">
          <input
            className="input"
            value={shortName}
            maxLength={12}
            onChange={(e) => {
              setShortName(e.target.value);
              if (badge && /^[A-Za-z0-9]{1,3}$/.test(e.target.value)) setBadge({ ...badge, text: e.target.value.toUpperCase() });
            }}
          />
        </Field>
        <Field label="Country">
          <input className="input" value={country} maxLength={56} onChange={(e) => setCountry(e.target.value)} />
        </Field>
      </div>
      <h3>Badge</h3>
      <BadgeEditor value={eff} onChange={setBadge} name={name} />
    </Modal>
  );
}

export function TeamDetailPage() {
  const { teamId } = useParams({ strict: false }) as { teamId: string };
  const id = Number(teamId);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const q = useQuery({ queryKey: ['team', id], queryFn: () => get<{ team: Team; squad: { id: number; name: string; position: string | null }[] }>(`/admin/teams/${id}`) });
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [newName, setNewName] = useState('');
  const [newPos, setNewPos] = useState('Attacker');
  const [search, setSearch] = useState('');
  const found = useQuery({
    queryKey: ['players', 'search', search],
    queryFn: () => get<{ players: Player[] }>(`/admin/players${qs({ q: search, limit: 10 })}`),
    enabled: search.trim().length > 1,
  });
  const { busy, run } = useAction();
  if (q.isLoading) return <Loading />;
  if (q.error) return <div className="page"><ErrorBox error={q.error} /></div>;
  const { team, squad } = q.data!;
  const refresh = () => qc.invalidateQueries({ queryKey: ['team', id] });
  return (
    <>
      <Topbar
        title={
          <span className="row">
            <Badge spec={team.badge} size={40} /> {team.name}
          </span>
        }
        sub={[team.shortName, team.country, team.provider === 'manual' ? 'created by admins' : 'from the data feed'].filter(Boolean).join(' · ')}
        actions={
          <>
            {team.provider !== 'manual' && (
              <button
                type="button"
                className="btn"
                disabled={busy}
                title="Name, country, colours and squad from the data feed (the badge design is kept)"
                onClick={() =>
                  run(async () => {
                    await post(`/admin/teams/${id}/refresh`);
                    await qc.invalidateQueries({ queryKey: ['team', id] });
                    await qc.invalidateQueries({ queryKey: ['teams'] });
                  }, 'Refreshed from the data feed')
                }
              >
                Refresh from the data feed
              </button>
            )}
            <button type="button" className="btn" onClick={() => setEditing(true)}>
              Edit team and badge
            </button>
            <button type="button" className="btn btn-danger" onClick={() => setConfirmDelete(true)}>
              Delete
            </button>
          </>
        }
      />
      <div className="page">
        <div className="cols cols-main">
          <section className="card" aria-labelledby="squad-h">
            <div className="card-head">
              <h2 id="squad-h">Squad</h2>
              <span className="muted small">Players here can be picked as first scorer in this team's matches.</span>
            </div>
            {squad.length === 0 ? (
              <Empty>No players yet.</Empty>
            ) : (
              <table className="table">
                <tbody>
                  {squad.map((p) => (
                    <tr key={p.id}>
                      <td>
                        <strong>{p.name}</strong>
                      </td>
                      <td className="muted">{p.position ?? '—'}</td>
                      <td className="right">
                        <button type="button" className="btn btn-sm" disabled={busy} onClick={() => run(async () => { await del(`/admin/teams/${id}/squad/${p.id}`); await refresh(); }, `${p.name} removed from the squad`)}>
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
          <div className="stack" style={{ gap: 20 }}>
            <section className="card" aria-labelledby="addp-h">
              <div className="card-head">
                <h2 id="addp-h">Add a new player</h2>
              </div>
              <form
                className="card-body stack"
                onSubmit={(e) => {
                  e.preventDefault();
                  void run(async () => {
                    await post('/admin/players', { name: newName, position: newPos, teamIds: [id] });
                    setNewName('');
                    await refresh();
                  }, 'Player added');
                }}
              >
                <Field label="Name">
                  <input className="input" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. D. Brennan" required />
                </Field>
                <Field label="Position">
                  <select className="select" value={newPos} onChange={(e) => setNewPos(e.target.value)}>
                    {['Goalkeeper', 'Defender', 'Midfielder', 'Attacker'].map((p) => (
                      <option key={p}>{p}</option>
                    ))}
                  </select>
                </Field>
                <button className="btn btn-primary" disabled={busy}>
                  Add to squad
                </button>
              </form>
            </section>
            <section className="card" aria-labelledby="existing-h">
              <div className="card-head">
                <h2 id="existing-h">Add an existing player</h2>
              </div>
              <div className="card-body stack">
                <input className="input" placeholder="Search players" aria-label="Search players" value={search} onChange={(e) => setSearch(e.target.value)} />
                {found.data?.players
                  .filter((p) => !squad.some((s) => s.id === p.id))
                  .map((p) => (
                    <div key={p.id} className="row">
                      <span className="grow">
                        {p.name} <span className="muted small">{p.teams.map((t) => t.name).join(', ')}</span>
                      </span>
                      <button type="button" className="btn btn-sm" disabled={busy} onClick={() => run(async () => { await put(`/admin/teams/${id}/squad/${p.id}`, {}); await refresh(); }, `${p.name} added`)}>
                        Add
                      </button>
                    </div>
                  ))}
                <span className="muted small">A player can belong to several teams, such as a club and a national team.</span>
              </div>
            </section>
            <Link to="/matches" search={{ q: team.name }} className="btn">
              See {team.name}'s matches
            </Link>
          </div>
        </div>
      </div>
      {editing && <TeamForm team={team} onClose={() => setEditing(false)} />}
      {confirmDelete && (
        <Confirm
          title={`Delete ${team.name}?`}
          body="Only a team with no matches can be deleted."
          confirmLabel="Delete team"
          danger
          onClose={() => setConfirmDelete(false)}
          onConfirm={async () => {
            await del(`/admin/teams/${id}`);
            await qc.invalidateQueries({ queryKey: ['teams'] });
            navigate({ to: '/teams' });
          }}
        />
      )}
    </>
  );
}

// ================================================================ players

export function PlayersPage() {
  const [q, setQ] = useState('');
  const [teamId, setTeamId] = useState('');
  const players = useQuery({ queryKey: ['players', q, teamId], queryFn: () => get<{ players: Player[] }>(`/admin/players${qs({ q, teamId })}`) });
  const teams = useQuery({ queryKey: ['teams', ''], queryFn: () => get<{ teams: Team[] }>('/admin/teams') });
  const [editing, setEditing] = useState<Player | 'new' | null>(null);
  return (
    <>
      <Topbar
        title="Players"
        sub="First-scorer picks come from these squads"
        actions={
          <button type="button" className="btn btn-primary" onClick={() => setEditing('new')}>
            New player
          </button>
        }
      >
        <input className="input" placeholder="Search players" aria-label="Search players" value={q} onChange={(e) => setQ(e.target.value)} style={{ width: 200 }} />
        <select className="select" aria-label="Team" value={teamId} onChange={(e) => setTeamId(e.target.value)}>
          <option value="">All teams</option>
          {teams.data?.teams.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
      </Topbar>
      <div className="page">
        <div className="card">
          {players.error && <ErrorBox error={players.error} />}
          {players.isLoading ? (
            <Loading />
          ) : players.data?.players.length === 0 ? (
            <Empty>No players found.</Empty>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Position</th>
                  <th>Teams</th>
                  <th>Source</th>
                </tr>
              </thead>
              <tbody>
                {players.data?.players.map((p) => (
                  <tr key={p.id} className="clickable" onClick={() => setEditing(p)}>
                    <td>
                      <strong>{p.name}</strong>
                    </td>
                    <td>{p.position ?? '—'}</td>
                    <td>{p.teams.map((t) => t.name).join(', ') || <span className="muted">No team</span>}</td>
                    <td className="muted">{p.provider === 'manual' ? 'Admin' : 'Data feed'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      {editing && <PlayerForm player={editing === 'new' ? null : editing} teams={teams.data?.teams ?? []} onClose={() => setEditing(null)} />}
    </>
  );
}

function PlayerForm({ player, teams, onClose }: { player: Player | null; teams: Team[]; onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState(player?.name ?? '');
  const [position, setPosition] = useState(player?.position ?? '');
  const [teamIds, setTeamIds] = useState<number[]>(player?.teams.map((t) => t.id) ?? []);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const { busy, run } = useAction();
  return (
    <Modal
      title={player ? `Edit ${player.name}` : 'New player'}
      onClose={onClose}
      footer={
        <>
          {player && (
            <button type="button" className="btn btn-danger" style={{ marginRight: 'auto' }} onClick={() => setConfirmDelete(true)}>
              Delete
            </button>
          )}
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy || !name.trim()}
            onClick={() =>
              run(async () => {
                const body = { name, position: position || null, teamIds };
                if (player) await patch(`/admin/players/${player.id}`, body);
                else await post('/admin/players', body);
                await qc.invalidateQueries({ queryKey: ['players'] });
                await qc.invalidateQueries({ queryKey: ['team'] });
                onClose();
              }, 'Player saved')
            }
          >
            Save
          </button>
        </>
      }
    >
      <div className="form-grid">
        <Field label="Name">
          <input className="input" value={name} maxLength={60} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Position">
          <select className="select" value={position} onChange={(e) => setPosition(e.target.value)}>
            <option value="">Unknown</option>
            {['Goalkeeper', 'Defender', 'Midfielder', 'Attacker'].map((p) => (
              <option key={p}>{p}</option>
            ))}
          </select>
        </Field>
      </div>
      <Field label="Teams" hint="Hold Ctrl or Cmd to pick several">
        <select
          className="select"
          multiple
          style={{ height: 180, padding: 6 }}
          value={teamIds.map(String)}
          onChange={(e) => setTeamIds([...e.target.selectedOptions].map((o) => Number(o.value)))}
        >
          {teams.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
      </Field>
      {confirmDelete && player && (
        <Confirm
          title={`Delete ${player.name}?`}
          body="A player who scored or was picked as first scorer can't be deleted; remove him from the squad instead."
          confirmLabel="Delete"
          danger
          onClose={() => setConfirmDelete(false)}
          onConfirm={async () => {
            await del(`/admin/players/${player.id}`);
            await qc.invalidateQueries({ queryKey: ['players'] });
            onClose();
          }}
        />
      )}
    </Modal>
  );
}
